import { logWarnOnce } from './log_once';

interface CanvasPool {
  [type: number]: {
    [type: number]: CanvasRenderingContext2D[];
  };
}

/**
 * Static class that holds a pool of offscreen canvases & 2d contexts on the assumption that they'll be used multiple times.
 *
 * Pedantic note: this is technically a CanvasRenderingContext2D (c2d) cache, but each c2d keeps a reference to it's canvas, so by caching the former we get both.
 *
 * The goal is to improve playback performance by reducing object creation and subsequent garbage collection.
 *
 * It also facilitates future improvements such as drawing a canvas once and re-using it across multiple frames.
 *
 * Implementation notes:
 * Canvases are only cached when in continuous use. Any canvas not used in a given frame is release to be garbage collected at the beginning of the next frame.
 * This is to prevent hanging on to lots of unused entries when something is animated from small to large.
 * There is room for improvement here, but it's probably good enough for now.
 *
 * Initially canvases were cached indefinitely and allowLarger was forced to true to handle animation, but this led to rendering bugs in code that depended on the exact size of the canvas for correct playback.
 *
 * A LRU cache was also considered but rejected due to the potential for a frame to use more canvases than fit in the cache, leading to cache thrashing.
 * Additionally, allowing caching of multiple canvases of the same size would make an LRU implementation more complex.
 */
export class OffscreenCanvasCache {
  // pool[width][height][index] = c2d
  private static pool: CanvasPool = {};
  private static prevPool: CanvasPool = {};

  private static outstandingCount = 0;

  // Height and width are rounded up to nearest multiple (when permitted) to improve the cache hit rate.
  private static multiple = 100;

  private static roundUp(num: number, multiple): number {
    const remainder = num % multiple;

    if (remainder === 0) {
      return num;
    }

    return num - remainder + multiple;
  }

  // check the current frame's pool and the previous frame's pool for a match
  private static getEntry(width: number, height: number): CanvasRenderingContext2D | undefined {
    return (
      this.getEntryFromPool(this.pool, width, height) ||
      this.getEntryFromPool(this.prevPool, width, height)
    );
  }

  private static getEntryFromPool(
    pool: CanvasPool,
    width: number,
    height: number,
  ): CanvasRenderingContext2D | undefined {
    const entries = pool[width]?.[height];
    if (entries?.length) {
      const entry = entries.pop();
      this.reset(entry);
      return entry;
    }
    return undefined;
  }

  private static create(width: number, height: number): CanvasRenderingContext2D {
    const canvas = document.createElement('canvas');
    // TODO: Scale the offscreen canvas. Every place that draws an offscreen canvas
    // will have to perform a transform first, draw, then perform a transform again.
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    context.save();
    return context;
  }

  private static reset(c2d: CanvasRenderingContext2D) {
    c2d.restore();
    c2d.save();
    c2d.clearRect(0, 0, c2d.canvas.width, c2d.canvas.height);
  }

  /**
   * Throw away any canvases that weren't used in the previous frame.
   * This should be called by viewrenderer at the start of each new frame.
   */
  static onNextFrame() {
    this.prevPool = this.pool;
    this.pool = [];
    if (this.outstandingCount !== 0) {
      logWarnOnce(
        `some offscreen canvases were leaked, which may impact performance. This warning will only be logged once.`,
      );
    }
  }

  /**
   * Get a 2D Canvas Rendering Context
   *
   * If a matching context is available in the pool, it will be reset and supplied, otherwise one will be created.
   *
   * If allowLarger is true, it may return a context who's associated canvas size is larger than the specified height and width.
   * Otherwise the requested size is guaranteed.
   *
   * allowLarger is recommended when possible as it improves the cache hit rate.
   *
   * It is recommended that the context be put back into the pool when it is no longer needed.
   * @see OffscreenCanvasCache.putC2d
   */
  static getC2d(
    widthArg: number,
    heightArg: number,
    options?: { allowLarger: boolean },
  ): CanvasRenderingContext2D {
    this.outstandingCount++;
    let width = widthArg;
    let height = heightArg;
    if (height <= 0 || width <= 0) {
      // This is generally an indication of a rendering operation that should be skipped entirely.
      logWarnOnce(
        `0-sized canvas requested. Returning ${this.multiple}x${this.multiple} canvas to avoid errors. This warning will only be logged once.`,
      );
      width = this.multiple;
      height = this.multiple;
    }

    // first check for a match at the exact size
    let entry = this.getEntry(width, height);
    if (entry) {
      return entry;
    }

    // next check at a rounded up size (if allowed)
    if (options?.allowLarger) {
      width = this.roundUp(width, this.multiple);
      height = this.roundUp(height, this.multiple);

      entry = this.getEntry(width, height);
      if (entry) {
        return entry;
      }
    }

    // if we don't have a matching cache entry, create a new canvas
    return this.create(width, height);
  }

  /**
   * Put a 2D Canvas Rendering Context back in the pool when it's use is complete.
   *
   * It is recommended that any references to the context or it's canvas be nulled after putting it into the pool.
   *
   * todo: consider asserting that the entry is not already in the cache to catch mistakes
   */
  static putC2d(c2d: CanvasRenderingContext2D) {
    this.outstandingCount--;
    const { width, height } = c2d.canvas;
    this.pool[width] ||= {};
    this.pool[width][height] ||= [];
    this.pool[width][height].push(c2d);
  }
}
